import {
  NavigationContainerName,
  NavigationEventListener,
  navigationContainers,
  navigationEvents,
} from "./navigation";
import {
  insert,
  nextItem,
  onSelect,
  remove,
  selectIsActiveElement,
} from "./navigationSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useCallback, useEffect, useId } from "react";

export const useNavigationKeys = () => {
  const dispatch = useAppDispatch();

  const onKeydown = useCallback((e: KeyboardEvent) => {
    if (
      e.key === "ArrowUp" ||
      e.key === "ArrowDown" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight"
    ) {
      dispatch(nextItem(e.key));
    }
    if (e.key === "Enter") {
      dispatch(onSelect("Enter key pressed!"));
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", onKeydown);
    return () => {
      window.removeEventListener("keydown", onKeydown);
    };
  }, []);
};

export const useNavigationGamepad = () => {
  const gamepads: { [index: number]: Gamepad } = {};

  function gamepadHandler(event: GamepadEvent, connecting: boolean) {
    const gamepad = event.gamepad;
    // Note:
    // gamepad === navigator.getGamepads()[gamepad.index]

    if (connecting) {
      gamepads[gamepad.index] = gamepad;
    } else {
      delete gamepads[gamepad.index];
    }
  }

  window.addEventListener(
    "gamepadconnected",
    (e) => {
      gamepadHandler(e, true);
    },
    false
  );
  window.addEventListener(
    "gamepaddisconnected",
    (e) => {
      gamepadHandler(e, false);
    },
    false
  );
};

export const useNavigation = (props: {
  onSelect: NavigationEventListener;
  name: string;
  position?: number;
  containerId?: NavigationContainerName;
  initialFocus?: boolean;
  focusOnMount?: boolean;
}) => {
  const { onSelect, name, initialFocus, focusOnMount } = props;

  const dispatch = useAppDispatch();
  const id = useId();
  const isActiveElement = useAppSelector((state) =>
    selectIsActiveElement(state, id)
  );

  const containerId = props.containerId || "root";
  const position = props.position || 0;
  const container = navigationContainers[containerId];

  useEffect(() => {
    dispatch(
      insert({
        id,
        position,
        name,
        containerId,
        container,
        initialFocus,
        focusOnMount,
      })
    );

    const subscription = navigationEvents.subscribe(id, onSelect);

    return () => {
      dispatch(remove(id));

      subscription.remove();
    };
  }, []);

  return { isActiveElement };
};
